import PropTypes from "prop-types";
import { m, useMotionValue, useTransform, useAnimationFrame } from "framer-motion";

// @mui
import Box from "@mui/material/Box";

// ----------------------------------------------------------------------

/**
 * Ring drawn behind the microphone icon while recording, for the places where there is no room for a
 * wider indicator (a text field or picker adornment): it keeps exactly the IconButton footprint.
 *
 * It samples the level from a ref inside framer-motion's frame loop and writes it to a motion value,
 * which updates the DOM without ever re-rendering React — a 60 fps setState here would re-render the
 * surrounding field (and its Controller) every frame. Mount it only while listening: the frame loop
 * lives for as long as this component is mounted, and a form can hold many microphones.
 */
export default function VoiceLevelHalo({ levelRef, size = 22, color = "error.main" }) {
  const level = useMotionValue(0);
  const scale = useTransform(level, [0, 1], [1, 1.8]);
  const opacity = useTransform(level, [0, 1], [0.2, 0.55]);

  useAnimationFrame(time => {
    // A slow breath keeps "listening" unmistakable even in silence, or when the browser gave us no
    // AudioContext at all; the measured level adds the reactive part on top. Speech rarely exceeds an
    // RMS of about 0.3, so it is scaled up to fill the ring at a normal speaking volume.
    const breath = 0.15 + 0.1 * Math.sin(time / 320);

    level.set(Math.min(1, breath + Math.min(1, (levelRef?.current ?? 0) * 3.5)));
  });

  return (
    <Box
      component={m.div}
      aria-hidden
      style={{ scale, opacity }}
      sx={{
        position: "absolute",
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: color,
        pointerEvents: "none",
      }}
    />
  );
}

VoiceLevelHalo.propTypes = {
  levelRef: PropTypes.object,
  size: PropTypes.number,
  color: PropTypes.string,
};
